import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";

const LN1_FAMILY_STRINGS = new Set(["ln1", "rvu", "cga", "lobbyvision"]);

const LN1_FAMILY_ENUMS = new Set<number>([
  AdapterProtocol.LN1,
  AdapterProtocol.RVU,
  AdapterProtocol.CGA,
  AdapterProtocol.LOBBYVISION,
]);

/**
 * Given a legacy per-unit offset array, return the 2-row canonical form.
 *   [] -> { initial: 0, delta: 0 }
 *   [a] -> { initial: a, delta: 0 }
 *   [a, b] -> { initial: a, delta: b - a }
 *   [a, b, c, ...] -> { initial: a, delta: b - a }  (ignore >= index 2)
 */
export function deriveInitialAndDelta(arr: number[] | undefined | null): {
  initial: number;
  delta: number;
} {
  if (!arr || arr.length === 0) {
    return { initial: 0, delta: 0 };
  }
  if (arr.length === 1) {
    return { initial: arr[0], delta: 0 };
  }
  return { initial: arr[0], delta: arr[1] - arr[0] };
}

/**
 * Expand initial+delta into an N-car dense array.
 *   expandOffsets(100, 121, 3) -> [100, 221, 342]
 *   expandOffsets(50, 0, 4) -> [50, 50, 50, 50]
 */
export function expandOffsets(initial: number, delta: number, numCars: number): number[] {
  return Array.from({ length: numCars }, (_, i) => initial + delta * i);
}

/**
 * True when the protocol identifier (string OR numeric AdapterProtocol enum)
 * is in the LN1 family: LN1, RVU, CGA, LOBBYVISION. Case-insensitive for strings.
 * Returns false for MODBUS, BACNET, OPC, MCE, etc.
 */
export function isLn1FamilyProtocol(proto: string | number | null | undefined): boolean {
  if (proto === null || proto === undefined) {
    return false;
  }
  if (typeof proto === "string") {
    return LN1_FAMILY_STRINGS.has(proto.toLowerCase());
  }
  return LN1_FAMILY_ENUMS.has(proto);
}

/**
 * UI-layer classification: is a stored bank cell a derived value or an override?
 * A cell is "derived" iff stored === derived; otherwise "overridden".
 */
export function classifyBankCell(stored: number, derived: number): "derived" | "overridden" {
  if (stored === derived) {
    return "derived";
  }
  return "overridden";
}

function normalizeEntryValue(val: unknown): [number, number] | null {
  if (typeof val === "number") {
    return [val, val];
  }
  if (Array.isArray(val)) {
    if (val.length === 0) {
      return [0, 0];
    }
    if (val.length === 1) {
      return [val[0] as number, val[0] as number];
    }
    return [val[0] as number, val[1] as number];
  }
  return null;
}

/**
 * Accept any of the legacy offset_mapping shapes the backend may return and
 * return a normalized Record<categoryKey, [initial, initial+delta]> shape.
 * Handled inputs:
 *   a) already-normalized object-of-arrays:   { service: [100, 221] }         -> pass through
 *   b) array-of-entries tuple shape:          [["service", [100, 221]]]        -> convert to object
 *   c) legacy bare-number shape:              { service: 100 }                 -> { service: [100, 100] }  (delta=0)
 *   d) legacy per-unit longer array:          { service: [100, 221, 342] }     -> { service: [100, 221] }
 *   e) null / undefined / other non-object:                                    -> {}
 * The function is IDEMPOTENT: migrate(migrate(x)) === migrate(x) for all x.
 */
export function migrateLegacyOffsetMap(raw: unknown): Record<string, [number, number]> {
  if (raw === null || raw === undefined) {
    return {};
  }

  // Handle array-of-entries shape: [["service", [100, 221]], ...]
  if (Array.isArray(raw)) {
    return (raw as unknown[]).reduce<Record<string, [number, number]>>((acc, entry) => {
      if (Array.isArray(entry) && entry.length >= 2 && typeof entry[0] === "string") {
        const normalized = normalizeEntryValue(entry[1]);
        if (normalized !== null) {
          return { ...acc, [entry[0]]: normalized };
        }
      }
      return acc;
    }, {});
  }

  if (typeof raw !== "object") {
    return {};
  }

  const asRecord = raw as Record<string, unknown>;
  return Object.keys(asRecord).reduce<Record<string, [number, number]>>((acc, key) => {
    const normalized = normalizeEntryValue(asRecord[key]);
    if (normalized !== null) {
      return { ...acc, [key]: normalized };
    }
    return acc;
  }, {});
}
